import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import useAuth from "@/hooks/auth.hook";
import usePlanFeatures from "@/hooks/plan.hook";
import { featureForModule, moduleForPath } from "@/utils/modules";

/**
 * Route-level access guard. Complements the sidebar filter (which only removes links) so typing a
 * URL can't reach a forbidden page:
 *   - /dashboard/manage/* is super-admin only (the backend also rejects these APIs).
 *   - a module a super-admin has hidden for the business is blocked (isModuleVisible).
 *   - a module the business's plan doesn't include is blocked (hasFeature).
 * Super-admins bypass all three. Renders nothing.
 */
const ModuleRouteGuard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const { hiddenModules, isModuleVisible, hasFeature, isPlanLoaded, features } = usePlanFeatures();

  useEffect(() => {
    // Management area is super-admin only.
    if (!isSuperAdmin && location.pathname.startsWith("/dashboard/manage")) {
      navigate("/dashboard", { replace: true });
      return;
    }
    const key = moduleForPath(location.pathname);
    if (key && !isModuleVisible(key)) {
      navigate("/dashboard", { replace: true });
      return;
    }
    // Plan gate. Only once entitlements have loaded — feature checks fail closed, and redirecting on
    // a not-yet-answered query would bounce a paying business off its own page on a hard refresh.
    if (key && isPlanLoaded && !hasFeature(featureForModule(key))) {
      navigate("/dashboard", { replace: true });
    }
    // isModuleVisible/hasFeature are derived from hiddenModules/features; depend on their serialized
    // forms so a refetch returning an equal object doesn't re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, hiddenModules.join(","), JSON.stringify(features), isSuperAdmin, isPlanLoaded, navigate]);

  return null;
};

export default ModuleRouteGuard;
